import { Identifier, MatchPlayerStatus, MatchResultPlayerStatus } from '@okletsplay/core';
import { MatchEntity, MatchResultEntity } from 'app/interfaces/entity';
import { sessionSelector } from 'app/store/session/selectors';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatchPlatformFeePercentage } from 'shared/hooks/use-platform-fee-percentage';
import { calculateMatchWinnings } from 'modules/game-payments/utils';

const DEFAULT_WINNINGS = 0;

export const useMatchResult = (match: MatchEntity | undefined) => {
  const user = useSelector(sessionSelector.activeUser);

  const platformFeeSettings = useMatchPlatformFeePercentage();
  const platformFeePercentage = MultiCurrencyConfig.isEnabledForMatches()
    ? 0
    : platformFeeSettings.platformFeePercentage;

  const players = useMemo(() => {
    return match?.players?.filter((player) =>
      [
        MatchPlayerStatus.Ready,
        MatchPlayerStatus.Invited,
        MatchPlayerStatus.Played,
        MatchPlayerStatus.Requested,
      ].includes(player.status)
    );
  }, [match]);

  const playerNamesMap: Record<Identifier, string> = useMemo(() => {
    if (!players) return {};

    return players?.reduce(
      (acc, player) => ({ ...acc, [player?.user?.userId]: player?.user?.userName }),
      {}
    );
  }, [players]);

  const wonUserIds = useMemo(() => {
    if (!match?.results) return [];

    return match.results
      .filter(
        (matchResult) =>
          [MatchResultPlayerStatus.Win, MatchResultPlayerStatus.Tied].includes(
            matchResult.status
          ) && matchResult.userId
      )
      .map((matchResult) => matchResult.userId);
  }, [match?.results]);

  const matchResultsMap: Record<Identifier, MatchResultEntity> = useMemo(() => {
    if (!match?.results) return {};

    const paidPlayers = match?.players?.filter((player) =>
      [MatchPlayerStatus.Played, MatchPlayerStatus.Ready].includes(player?.status)
    );

    const winnings = match.entryFee
      ? calculateMatchWinnings(
          match.entryFee,
          platformFeePercentage,
          wonUserIds.length,
          paidPlayers.length
        )
      : DEFAULT_WINNINGS;

    return match.results.reduce(
      (acc, matchResult) => ({
        ...acc,
        [matchResult.userId]: {
          ...matchResult,
          winnings: wonUserIds.includes(matchResult.userId) ? winnings : DEFAULT_WINNINGS,
        },
      }),
      {}
    );
  }, [match?.results, match?.players, match?.entryFee, platformFeePercentage, wonUserIds]);

  const isCurrentPlayerWinner: boolean = useMemo(() => {
    if (!user?.userId) return false;

    return wonUserIds.includes(user.userId);
  }, [wonUserIds, user?.userId]);

  const currentPlayerWinnings = useMemo(() => {
    if (!user?.userId) return DEFAULT_WINNINGS;

    return matchResultsMap[user.userId]?.winnings ?? DEFAULT_WINNINGS;
  }, [matchResultsMap, user.userId]);

  return {
    players,
    playerNamesMap,
    matchResultsMap,
    isCurrentPlayerWinner,
    currentPlayerWinnings,
  };
};
